const DEVICE_STORAGE_KEY = 'bajie_sni_sessions'
const ACCOUNT_STORAGE_KEY = 'bajie_sni_account_session'

function resolveStorage (options = {}) {
  if (options.storage) return options.storage
  return typeof window === 'undefined' ? null : window.localStorage
}

function readJson (key, fallback, options) {
  const storage = resolveStorage(options)
  if (!storage) return fallback
  try {
    return JSON.parse(storage.getItem(key) || JSON.stringify(fallback))
  } catch (error) {
    return fallback
  }
}

function writeJson (key, value, options) {
  const storage = resolveStorage(options)
  if (!storage) return
  try {
    storage.setItem(key, JSON.stringify(value))
  } catch (error) {
    // localStorage can be unavailable in restricted browser modes.
  }
}

function removeItem (key, options) {
  const storage = resolveStorage(options)
  if (!storage) return
  try {
    storage.removeItem(key)
  } catch (error) {
    // localStorage can be unavailable in restricted browser modes.
  }
}

function readDeviceSessions (options) {
  return readJson(DEVICE_STORAGE_KEY, {}, options)
}

function normalizeDeviceList (devices) {
  return Array.isArray(devices)
    ? devices.map(device => ({
      ...device,
      sn: normalizeSniSn(device && device.sn)
    })).filter(device => device.sn)
    : []
}

function normalizeAccountList (accounts) {
  return Array.isArray(accounts) ? accounts.filter(Boolean).map(account => ({ ...account })) : []
}

function resolveLoginDeviceSn (data) {
  if (data && Object.prototype.hasOwnProperty.call(data, 'deviceSn')) {
    return normalizeSniSn(data.deviceSn)
  }
  return normalizeSniSn(data && data.deviceList && data.deviceList[0] && data.deviceList[0].sn)
}

export function normalizeSniSn (sn) {
  return String(sn || '').trim().toUpperCase()
}

export function isValidSniSn (sn) {
  return !!normalizeSniSn(sn)
}

export function getSniSession (sn, options = {}) {
  const normalizedSn = normalizeSniSn(sn)
  if (!normalizedSn) return null
  return readDeviceSessions(options)[normalizedSn] || null
}

export function saveSniSession (sn, session, options = {}) {
  const normalizedSn = normalizeSniSn(sn)
  if (!normalizedSn || !session || !session.token) return null

  const sessions = readDeviceSessions(options)
  sessions[normalizedSn] = {
    ...session,
    sn: normalizedSn,
    deviceSn: normalizeSniSn(session.deviceSn) || normalizedSn,
    profile: session.profile ? { ...session.profile } : null,
    accountList: normalizeAccountList(session.accountList),
    deviceList: normalizeDeviceList(session.deviceList),
    updatedAt: Date.now()
  }
  writeJson(DEVICE_STORAGE_KEY, sessions, options)
  return sessions[normalizedSn]
}

export function clearSniSession (sn, options = {}) {
  const normalizedSn = normalizeSniSn(sn)
  if (!normalizedSn) return
  const sessions = readDeviceSessions(options)
  delete sessions[normalizedSn]
  writeJson(DEVICE_STORAGE_KEY, sessions, options)
}

export function getSniAccountSession (options = {}) {
  const session = readJson(ACCOUNT_STORAGE_KEY, null, options)
  return session && session.token ? session : null
}

export function saveSniAccountSession (session, options = {}) {
  if (!session || !session.token) return null
  const profile = session.profile && typeof session.profile === 'object' ? session.profile : null
  const profileCompleted = session.profileCompleted !== undefined
    ? session.profileCompleted
    : profile && profile.profileCompleted
  const normalized = {
    ...session,
    deviceSn: resolveLoginDeviceSn(session) || null,
    profile,
    profileCompleted,
    accountList: normalizeAccountList(session.accountList),
    deviceList: normalizeDeviceList(session.deviceList),
    updatedAt: Date.now()
  }
  writeJson(ACCOUNT_STORAGE_KEY, normalized, options)
  return normalized
}

export function getActiveSniSession (sn, options = {}) {
  return getSniSession(sn, options) || getSniAccountSession(options)
}

export function getSniStorageScope (sn, options = {}) {
  const normalizedSn = normalizeSniSn(sn)
  if (normalizedSn) return normalizedSn
  const session = getSniAccountSession(options)
  const accountId = session && (session.mobileAccountId || session.phoneNumber || (session.account && session.account.userId))
  return accountId === undefined || accountId === null || accountId === '' ? 'ACCOUNT' : `ACCOUNT-${accountId}`
}

export function applySniLoginResult (data, options = {}) {
  if (!data || !data.token) return null
  const profile = data.profile && typeof data.profile === 'object'
    ? data.profile
    : data.account && typeof data.account === 'object'
      ? data.account
      : null
  const deviceSn = resolveLoginDeviceSn(data)
  const normalized = {
    ...data,
    deviceSn: deviceSn || null,
    profile,
    deviceList: normalizeDeviceList(data.deviceList),
    accountList: normalizeAccountList(data.accountList),
    profileCompleted: data.profileCompleted !== undefined
      ? data.profileCompleted
      : profile && profile.profileCompleted
  }

  saveSniAccountSession(normalized, options)
  if (deviceSn) saveSniSession(deviceSn, normalized, options)
  return normalized
}

export function extractSniProfile (data) {
  if (!data || typeof data !== 'object') return {}
  if (Object.prototype.hasOwnProperty.call(data, 'profile')) return data.profile || {}
  if (Object.prototype.hasOwnProperty.call(data, 'account')) return data.account || {}
  return data
}

export function applySniCurrentResult (data, fallbackToken, options = {}) {
  if (!data || typeof data !== 'object') return null
  const token = data.token || fallbackToken
  if (!token) return extractSniProfile(data)
  return applySniLoginResult({
    ...data,
    token,
    profile: data.profile || extractSniProfile(data)
  }, options)
}

export function updateSniProfile (profile, sn, options = {}) {
  if (!profile) return
  const accountSession = getSniAccountSession(options)
  const profileCompleted = profile.profileCompleted !== undefined
    ? profile.profileCompleted
    : accountSession && accountSession.profileCompleted

  if (accountSession) {
    const mergedProfile = {
      ...(accountSession.profile || {}),
      ...profile
    }
    const accountList = normalizeAccountList(accountSession.accountList).map(account => ({
      ...account,
      ...mergedProfile,
      userId: account.userId
    }))
    saveSniAccountSession({
      ...accountSession,
      profileCompleted,
      profile: mergedProfile,
      account: accountSession.account
        ? { ...accountSession.account, ...mergedProfile }
        : null,
      accountList
    }, options)
  }

  const deviceSn = normalizeSniSn(sn) || (accountSession && accountSession.deviceSn)
  const deviceSession = getSniSession(deviceSn, options)
  if (deviceSession) {
    const mergedProfile = {
      ...(deviceSession.profile || {}),
      ...profile
    }
    saveSniSession(deviceSn, {
      ...deviceSession,
      profileCompleted,
      profile: mergedProfile,
      account: {
        ...(deviceSession.account || {}),
        ...mergedProfile
      }
    }, options)
  }
}

export function clearSniAccountSession (options = {}) {
  removeItem(ACCOUNT_STORAGE_KEY, options)
}

export function clearAllSniSessions (options = {}) {
  removeItem(ACCOUNT_STORAGE_KEY, options)
  removeItem(DEVICE_STORAGE_KEY, options)
}
